/**
 * Claude sources, one door, two shapes:
 * - claude.ai data-export zips: `memories.json` (newer exports carry the
 *   categorized memory entries), everything else in the zip ignored.
 * - Claude Code trees: `CLAUDE.md` (instructions), auto-memory
 *   `projects/<slug>/memory/MEMORY.md` (index) + topic files. Topic files win
 *   over their index lines; index lines survive only for absent topics.
 */
import { chunkMarkdown } from '../chunk.js';
import type { MemoryImportItem, NamedText } from '../types.js';
import { epistemicForHeading, parseGenericJson, plainTextItems } from './generic.js';
import { basename, frontmatterDescription } from './util.js';

const IGNORED_JSON = new Set(['users.json', 'projects.json', 'conversations.json']);
const INDEX_LINE = /^\s*[-*]\s*\[[^\]]*\]\(([^)]+)\)/;

export function parseClaude(files: NamedText[]): MemoryImportItem[] {
  const items: MemoryImportItem[] = [];
  const mdBasenames = new Set(files.filter((f) => /\.md$/i.test(f.name)).map((f) => basename(f.name).toLowerCase()));

  for (const file of files) {
    const base = basename(file.name).toLowerCase();
    if (base === 'memories.json') {
      items.push(...parseGenericJson(file.name, file.text));
    } else if (IGNORED_JSON.has(base) || !/\.md$/i.test(file.name)) {
      continue;
    } else if (base === 'claude.md' || base === 'claude.local.md') {
      items.push(...chunkMarkdown(file.text, basename(file.name), { epistemic: 'preference', epistemicForHeading }));
    } else if (base === 'memory.md') {
      // Index file: drop pointer lines whose topic file is part of this upload.
      const kept = file.text
        .split('\n')
        .filter((line) => {
          const match = INDEX_LINE.exec(line);
          if (!match) return true;
          return !mdBasenames.has(basename(match[1]!).toLowerCase());
        })
        .join('\n');
      items.push(...chunkMarkdown(kept, basename(file.name), { epistemicForHeading }));
    } else {
      const description = frontmatterDescription(file.text);
      if (description) items.push(...plainTextItems(description, { sourceDetail: basename(file.name) }));
      items.push(...chunkMarkdown(file.text, basename(file.name), { epistemicForHeading }));
    }
  }
  return items;
}
